import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { line } from 'd3-shape';
import { useEffect, useMemo, useState } from 'react';
import { usePlotContext } from '../hooks';
import { getNextId, validateAxis } from '../utils';
import ErrorBars from './ErrorBars';
import ScatterSeries from './ScatterSeries';
import { useLegend } from './legendsContext';
export default function LineSeries(props) {
    var _a;
    const [, legendDispatch] = useLegend();
    const { colorScaler } = usePlotContext();
    const [id] = useState(() => props.groupId || `series-${getNextId()}`);
    const { lineStyle = {}, displayMarker = false, displayErrorBars = false, hidden, ...otherProps } = props;
    useEffect(() => {
        var _a, _b;
        if (!hidden) {
            legendDispatch({
                type: 'ADD_LEGEND_LABEL',
                payload: {
                    id,
                    label: otherProps.label,
                    colorLine: ((_a = lineStyle === null || lineStyle === void 0 ? void 0 : lineStyle.stroke) === null || _a === void 0 ? void 0 : _a.toString()) || colorScaler(id),
                    shape: {
                        color: ((_b = otherProps.markerStyle) === null || _b === void 0 ? void 0 : _b.fill.toString()) || colorScaler(id),
                        figure: otherProps.markerShape || 'circle',
                        hidden: !displayMarker,
                    },
                },
            });
        }
        return () => legendDispatch({ type: 'REMOVE_LEGEND_LABEL', payload: { id } });
    }, [
        hidden,
        colorScaler,
        displayMarker,
        id,
        legendDispatch,
        lineStyle === null || lineStyle === void 0 ? void 0 : lineStyle.stroke,
        otherProps.label,
        otherProps.markerShape,
        otherProps.markerStyle,
        (_a = otherProps.markerStyle) === null || _a === void 0 ? void 0 : _a.fill,
    ]);
    if (hidden)
        return null;
    const lineProps = {
        id,
        data: props.data,
        xAxis: props.xAxis || 'x',
        yAxis: props.yAxis || 'y',
        lineStyle,
    };
    const errorBarsProps = {
        data: props.data,
        xAxis: props.xAxis || 'x',
        yAxis: props.yAxis || 'y',
        hidden: !displayErrorBars,
        style: props.errorBarsStyle,
        capStyle: props.errorBarsCapStyle,
        capSize: props.errorBarsCapSize,
    };
    return (_jsxs("g", { children: [_jsx(LineSeriesRender, Object.assign({}, lineProps), void 0),
            _jsx(ErrorBars, Object.assign({}, errorBarsProps), void 0),
            _jsx(ScatterSeries, Object.assign({}, otherProps, { hidden: !displayMarker, groupId: id }), void 0)] }, void 0));
}
function LineSeriesRender({ id, data, xAxis, yAxis, lineStyle, }) {
    // Get scales from context
    const { axisContext, colorScaler } = usePlotContext();
    const [xScale, yScale] = validateAxis(axisContext, xAxis, yAxis);
    // calculates the path to display
    const color = colorScaler(id);
    const path = useMemo(() => {
        if ([xScale, yScale].includes(undefined))
            return null;
        // Calculate line from D3
        const lineGenerator = line()
            .x((d) => xScale(d.x))
            .y((d) => yScale(d.y));
        return lineGenerator(data);
    }, [data, xScale, yScale]);
    if (!path)
        return null;
    // default style
    const style = {
        stroke: color,
        strokeWidth: 2,
        ...lineStyle,
    };
    return _jsx("path", { style: style, d: path, fill: "none" }, void 0);
}
//# sourceMappingURL=LineSeries.js.map